import { CommonModule } from '@angular/common';
import {
  Component,
  effect,
  ElementRef,
  EventEmitter,
  forwardRef,
  inject,
  Input,
  Output,
  signal,
  ViewChild,
  WritableSignal,
} from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatAutocompleteModule, MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { MatInputModule } from '@angular/material/input';
import { TranslateModule } from '@ngx-translate/core';
import {
  GalaxyAbstractFilterInput,
  GalaxyFilterOperator,
  GalaxyFilterValueInterface,
} from '@vendasta/galaxy/filter/chips';
import { GalaxyFormFieldModule } from '@vendasta/galaxy/form-field';
import { MatChipGrid, MatChipInput, MatChipInputEvent, MatChipRemove, MatChipRow } from '@angular/material/chips';
import { MatIcon } from '@angular/material/icon';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { COMMA, ENTER } from '@angular/cdk/keycodes';
import { CrmInjectionToken, SimplifiedUser, UserService } from '../../../tokens-and-interfaces';
import { catchError, debounceTime, startWith, switchMap, take, tap } from 'rxjs/operators';
import { Observable, of } from 'rxjs';

@Component({
  template: `
    @defer (when loadingValues() === false) {
      @if (singleInput()) {
        <glxy-form-field bottomSpacing="'none'">
          <input
            type="text"
            [placeholder]="'TABLE_FILTERS.SELECT_EXPRESSION_PLACEHOLDER' | translate"
            matInput
            [formControl]="formControl"
            [matAutocomplete]="auto"
            data-testid="filter-owner-input"
          />
          <mat-autocomplete
            #auto="matAutocomplete"
            (optionSelected)="selected($event)"
            [displayWith]="displayFn(availableOptions())"
          >
            <mat-option *ngFor="let option of availableOptions()" [value]="option?.userId">
              {{ option?.displayName }}
            </mat-option>
          </mat-autocomplete>
        </glxy-form-field>
      } @else {
        <glxy-form-field bottomSpacing="'none'">
          <mat-chip-grid #teamMemberGrid aria-label="Owner selection">
            <ng-container *ngIf="loadingValues(); else loadedChips">
              <mat-spinner [diameter]="24"></mat-spinner>
            </ng-container>
            <ng-template #loadedChips>
              <mat-chip-row *ngFor="let owner of selectedTeamMembers()" (removed)="remove(owner.userId)">
                {{ owner.displayName }}
                <button matChipRemove [attr.aria-label]="'remove ' + owner.displayName">
                  <mat-icon>cancel</mat-icon>
                </button>
              </mat-chip-row>
            </ng-template>
            <input
              matInput
              #ownerInput
              [placeholder]="'TABLE_FILTERS.SELECT_EXPRESSION_PLACEHOLDER' | translate"
              [formControl]="formControl"
              [matChipInputFor]="teamMemberGrid"
              [matAutocomplete]="auto"
              [matChipInputSeparatorKeyCodes]="separatorKeysCodes"
              [matChipInputAddOnBlur]="false"
              (matChipInputTokenEnd)="add($event)"
              data-testid="filter-owner-input"
            />
          </mat-chip-grid>
          <mat-autocomplete #auto="matAutocomplete" (optionSelected)="selected($event)">
            <mat-option *ngFor="let option of availableOptions()" [value]="option?.userId">
              {{ option?.displayName }}
            </mat-option>
          </mat-autocomplete>
        </glxy-form-field>
      }
    } @placeholder (minimum 100ms) {
      <div class="stencil-shimmer"></div>
    }
  `,
  styleUrls: ['./team-member-filter-input.component.scss'],
  providers: [
    {
      provide: GalaxyAbstractFilterInput,
      useExisting: forwardRef(() => TeamMemberFilterInputComponent),
    },
  ],
  imports: [
    CommonModule,
    TranslateModule,
    ReactiveFormsModule,
    MatInputModule,
    GalaxyFormFieldModule,
    MatAutocompleteModule,
    MatChipGrid,
    MatChipInput,
    MatChipRemove,
    MatChipRow,
    MatIcon,
    MatProgressSpinner,
  ],
})
export class TeamMemberFilterInputComponent implements GalaxyAbstractFilterInput {
  @ViewChild('ownerInput') ownerInput: ElementRef<HTMLInputElement>;
  separatorKeysCodes: number[] = [ENTER, COMMA];

  @Input() set fieldId(value: string) {
    this._fieldId.set(value);
  }

  @Output() valueChanges = new EventEmitter<GalaxyFilterValueInterface[]>();
  formControl: FormControl<SimplifiedUser | string> = new FormControl<SimplifiedUser | string>('', {
    nonNullable: true,
  });

  searchTerm = toSignal(this.formControl.valueChanges, { initialValue: '' });
  _fieldId = signal('');
  loadingValues = signal(true);
  availableOptions = signal<SimplifiedUser[]>([]);

  @Input() set initialValue(value: GalaxyFilterValueInterface[]) {
    this.loadingValues.set(true);
    const ownerIds = (value ?? []).map((v) => v.string);
    this.selectedTeamMemberIDs.set(ownerIds);
    const singleInput = this.singleInput();
    if (ownerIds.length > 0 && singleInput) {
      this.formControl.setValue(ownerIds[0]);
    }
  }
  singleInput: WritableSignal<boolean> = signal(true);
  _operator?: GalaxyFilterOperator;
  @Input() set operator(value: GalaxyFilterOperator) {
    this._operator = value;
    const isMultiInput =
      this._operator == GalaxyFilterOperator.FILTER_OPERATOR_IS_ANY ||
      this._operator == GalaxyFilterOperator.FILTER_OPERATOR_IS_NOT_ANY;
    if (isMultiInput) {
      this.formControl.setValue('');
    } else {
      const selectedTeamMember = this.selectedTeamMemberIDs();
      if (selectedTeamMember.length > 0) {
        this.formControl.setValue(selectedTeamMember[0]);
        if (selectedTeamMember.length > 1) {
          this.selectedTeamMemberIDs.set([selectedTeamMember[0]]);
        }
        this.userService
          .getMultiUsers(selectedTeamMember)
          .pipe(
            take(1),
            catchError(() => {
              return of([]);
            }),
            tap((sUsr) => {
              this.selectedTeamMembers.set(sUsr);
            }),
          )
          .subscribe();
      }
    }
    this.singleInput.set(!isMultiInput);
  }

  selectedTeamMemberIDs = signal<string[]>([]);
  selectedTeamMembers = signal<SimplifiedUser[]>([]);

  private readonly config = inject(CrmInjectionToken);
  private readonly userService: UserService;

  constructor() {
    if (this.config.services?.userService === undefined) {
      throw new Error('UserService is not provided');
    }
    this.userService = this.config.services?.userService;
    effect(() => {
      const selectedTeamMemberIDs = this.selectedTeamMemberIDs();
      this.valueChanges.emit(selectedTeamMemberIDs.map((s) => ({ string: s })));
    });

    this.formControl.valueChanges
      .pipe(
        startWith(''),
        takeUntilDestroyed(),
        debounceTime(300),
        switchMap((searchValue) => {
          return this.searchUsers(searchValue);
        }),
        tap((results) => {
          this.availableOptions.set(results);
          const isMultiInput =
            this._operator == GalaxyFilterOperator.FILTER_OPERATOR_IS_ANY ||
            this._operator == GalaxyFilterOperator.FILTER_OPERATOR_IS_NOT_ANY;
          if (isMultiInput) {
            const selectedTeamMember = this.selectedTeamMemberIDs();
            this.userService
              .getMultiUsers(selectedTeamMember)
              .pipe(
                take(1),
                catchError(() => {
                  return of([]);
                }),
              )
              .subscribe((sUsr) => {
                this.selectedTeamMembers.set(sUsr);
              });
          }
        }),
        tap(() => {
          this.loadingValues.set(false);
        }),
      )
      .subscribe();
  }

  searchUsers(searchTerm): Observable<SimplifiedUser[]> {
    const filterTerm = typeof searchTerm === 'string' ? searchTerm : searchTerm?.userId;
    if (searchTerm.startsWith('U-') && searchTerm.length === 38) {
      return this.userService.getMultiUsers([searchTerm]);
    }
    return this.userService.searchUsers(filterTerm);
  }

  clearValue() {
    this.formControl.setValue('');
  }

  displayFn(availableOptions: SimplifiedUser[]): (teamMember: string) => string {
    return (teamMember: string): string => {
      if (!teamMember) {
        return '';
      }
      const found = availableOptions.find((tm) => tm.userId === teamMember);
      return found?.displayName || teamMember;
    };
  }

  add(event: MatChipInputEvent) {
    const current = this.selectedTeamMemberIDs();
    const hasTeamMember = current.some((tm) => tm === event?.value);
    if (event?.value && !hasTeamMember) {
      this.selectedTeamMemberIDs.set([...current, event.value]);
    }

    event.chipInput?.clear();
    this.formControl.setValue('');
  }

  selected(event: MatAutocompleteSelectedEvent): void {
    const singleInput = this.singleInput();
    const current = this.selectedTeamMemberIDs();
    const hasTeamMember = current.some((tm) => tm === event.option.value);
    if (event.option.value && !hasTeamMember) {
      if (singleInput) {
        this.selectedTeamMemberIDs.set([event.option.value]);
      } else {
        this.selectedTeamMemberIDs.set([...current, event.option.value]);
      }
    }
    if (!singleInput) {
      this.ownerInput.nativeElement.value = '';
      this.formControl.setValue('');
    }
  }

  remove(userId: string) {
    const currentSalespeople = this.selectedTeamMemberIDs();
    const listWithoutSelected = currentSalespeople.filter((tm) => tm !== userId);
    this.selectedTeamMemberIDs.set([...listWithoutSelected]);
  }
}
